// Integration onboarding-session api fns. The two readers decode base64-wrapped
// form values and return different envelopes. The step write encodes form_data
// on the way in and returns the unwrapped body with the decoded session.
import type {
  CreateIntegrationOnboardingSessionRequest,
  CreateIntegrationOnboardingSessionResponse,
  GetIntegrationOnboardingSessionsQueryParams,
  IntegrationOnboardingStepExecutionRequest,
  IntegrationOnboardingStepExecutionResponse,
  SearchIntegrationOnboardingSessionsResponse,
} from '../../contracts/types';
import { http } from './connectedAppsHttp';
import {
  transformLocalUrlsToBase64,
  transformOnboardingStepBase64Entries,
} from './encoding';

export const createIntegrationOnboardingSession = (
  request: CreateIntegrationOnboardingSessionRequest
) => {
  return http().post<CreateIntegrationOnboardingSessionResponse>(
    `/integrations/onboardings/sessions`,
    request
  );
};

export const deleteIntegrationOnboardingSession = ({
  sessionId,
}: {
  sessionId: string;
}) => {
  return http().delete<null>(`/integrations/onboardings/sessions/${sessionId}`);
};

export const getIntegrationOnboardingSessionById = async (
  onboardingSessionId: string
) => {
  const data = await http().get<CreateIntegrationOnboardingSessionResponse>(
    `/integrations/onboardings/sessions/${onboardingSessionId}`
  );
  return {
    data: transformOnboardingStepBase64Entries(data.data),
  };
};

export const getIntegrationOnboardingSessions = async ({
  params,
}: {
  params: GetIntegrationOnboardingSessionsQueryParams;
}) => {
  const { data } = await http().get<SearchIntegrationOnboardingSessionsResponse>(
    `/integrations/onboardings/sessions`,
    {
      params,
    }
  );

  return {
    ...data,
    sessions: data.sessions.map((session) =>
      transformOnboardingStepBase64Entries(session)
    ),
  };
};

export const updateIntegrationOnboardingSessionStep = async ({
  session_id,
  step_id,
  data,
  signal,
}: {
  session_id: string;
  step_id: string;
  data: IntegrationOnboardingStepExecutionRequest;
  signal?: AbortSignal;
}) => {
  const transformedData = {
    ...data,
    payload: data.payload
      ? 'form_data' in data.payload
        ? {
            ...data.payload,
            form_data: transformLocalUrlsToBase64(
              (data.payload.form_data ?? {}) as Record<string, unknown>
            ),
          }
        : data.payload
      : undefined,
  };

  const endpoint = `integrations/onboardings/sessions/${session_id}/step_executions/${step_id}`;
  const { data: result } = signal
    ? await http().put<IntegrationOnboardingStepExecutionResponse>(
        endpoint,
        transformedData,
        {
          signal,
        }
      )
    : await http().put<IntegrationOnboardingStepExecutionResponse>(
        endpoint,
        transformedData
      );

  return {
    ...result,
    updated_session: transformOnboardingStepBase64Entries(result.updated_session),
  };
};
